import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrgKey } from '@reflex/ui/client/context';
import { githubApi, type GitHubAppView } from './api';

/**
 * Query key for the org-scoped GitHub Apps list. `/github/apps` resolves
 * against the caller's active org on the server, and the app-level QueryClient
 * survives an org switch, so the key must carry the org segment — the same
 * pattern as `useGitHubRepos` / `useGitHubBranches`. Without it, one org's
 * GitHub App installation list is served from cache inside another org until
 * the refetch lands.
 */
export function githubAppsQueryKey(orgKey: string) {
  return ['github', 'apps', orgKey] as const;
}

export function useGitHubApps() {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: githubAppsQueryKey(orgKey),
    queryFn: () => githubApi.listGitHubApps(),
  });
}

export interface GitHubAppInstallationState {
  /** The apps query is still loading; callers should not gate on it yet. */
  isLoading: boolean;
  /**
   * The apps query failed. Callers must NOT treat this as "no installation" —
   * a transient error would otherwise block a valid GitHub-node flow and show
   * the wrong remedy. Fail open: gate only when `!isLoading && !isError`.
   */
  isError: boolean;
  /**
   * At least one GitHub App is registered for this deployment. When false, no
   * App exists at all and an administrator must complete service setup before
   * anyone can install — a different remedy than "install the registered App".
   */
  appRegistered: boolean;
  /**
   * At least one non-suspended installation is visible under the active org.
   * This is the gate for "can a GitHub-dependent operation authenticate": a
   * suspended-only or install-less org resolves to `false`.
   */
  hasActiveInstallation: boolean;
  /**
   * The App to install against (the first registered one). `null` when none is
   * registered. Deployments run a single App in practice, so "first" is "the".
   */
  defaultApp: GitHubAppView | null;
}

/**
 * Derives the org's GitHub App installation state from {@link useGitHubApps},
 * so every surface that gates on "does this org have the App installed" — the
 * flow-publish modal, the blueprint repo picker — reads it the same way instead
 * of re-deriving the `installations`/`suspendedAt` logic inline.
 */
export function useGitHubAppInstallation(): GitHubAppInstallationState {
  const { data, isLoading, isError } = useGitHubApps();
  // Defensive: `data` is typed `GitHubAppView[]`, but a failed/parsed-wrong
  // response could hand back a non-array. Never let that crash a page that
  // merely gates on installation state.
  const apps = Array.isArray(data) ? data : [];
  return {
    isLoading,
    isError,
    appRegistered: apps.length > 0,
    // `?.` guards a malformed element missing `installations` (the contract
    // makes it required, but "never crash a gating page" means not trusting it).
    hasActiveInstallation: apps.some((app) =>
      app.installations?.some((inst) => inst.suspendedAt == null),
    ),
    defaultApp: apps[0] ?? null,
  };
}

export function useDeleteGitHubApp() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (id: string) => githubApi.deleteGitHubApp(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: githubAppsQueryKey(orgKey) }),
  });
}

export function useRemoveGitHubInstallation() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (installationId: number) => githubApi.removeGitHubInstallation(installationId),
    onSuccess: () => qc.invalidateQueries({ queryKey: githubAppsQueryKey(orgKey) }),
  });
}
